import moment, { Moment } from "moment";
import { DateFormat } from "../constants";
import { StringHelper } from "./StringHelper";

interface DateOptions {
  format?: DateFormat;
}

interface RangeDateOptions extends DateOptions {
  symbol?: string;
}

export const DateHelper = {
  // Input: moment or 'ISOString'
  // Output: DD-MM-YYYY
  formatDateToString(date: any, options?: DateOptions): string {
    const dateFormat = options?.format ?? DateFormat.DD_MM_YYY;
    if (!date) return "";
    if (moment.isMoment(date)) return date.format(dateFormat);
    if (typeof date === "string") {
      return moment(date).format(dateFormat);
    }
    return "";
  },
  // Input: [moment, moment] or 'ISOString|ISOString'
  // Output: 'DD-MM-YYYY - DD-MM-YYYY'
  formatRangeDateToString(range: any, options?: RangeDateOptions): string {
    const dateFormat = options?.format ?? DateFormat.DD_MM_YYY;
    const symbol = options?.symbol ?? "-";
    if (!range) return "";
    if (Array.isArray(range) && range.length >= 2) {
      const start = (range[0] as Moment).clone().format(dateFormat);
      const end = (range[1] as Moment).clone().format(dateFormat);
      return `${start} ${symbol} ${end} `;
    }
    if (typeof range === "string" && range.indexOf("|")) {
      const [start, end] = StringHelper.verticalBarSplit<string[]>(range);
      const startDate = moment(start).format(DateFormat.DD_MM_YYY);
      const endDate = moment(end).format(DateFormat.DD_MM_YYY);
      return `${startDate} ${symbol} ${endDate}`;
    }
    return "";
  },
  formatDateToISOStr(date: any): string {
    if (moment.isMoment(date)) {
      return date.toISOString();
    }
    return "";
  },
  formatRangeDateToISOStr(range: any, options?: Omit<RangeDateOptions, "format">) {
    const symbol = options?.symbol ?? "|";
    const [start, end] = range as [Moment, Moment];
    return `${start.toISOString()}${symbol || "|"}${end.toISOString()}`;
  },
  rangeDateListToISOStr(datesList: Moment[][]) {
    return datesList.map((i) => i.map((e) => e.toISOString()));
  },
  rangeDateListToMoment(datesList: string[][]) {
    return datesList.map((i) => i.map((e) => moment(e)));
  },
  formatDateToMoment(date: string): Moment | null {
    if (!date) return null;
    return moment(date);
  },
  formatRangeDateToMoment(range: string): Array<Moment> | null {
    if (!range) return null;
    const [start, end] = StringHelper.verticalBarSplit<string[]>(range);
    return [moment(start), moment(end)];
  },
  rangeDateStringifyToMoment(rangeDateStr: string): any[] {
    if (!rangeDateStr || typeof rangeDateStr !== "string") return [];
    const rangeDate: string[] = JSON.parse(rangeDateStr);
    return rangeDate.map((i) => moment(i));
  },
  getIsoStrNow(): string {
    return moment().toISOString();
  },
  getNow(): string {
    return moment().format(DateFormat.DD_MM_YYY);
  },

  countDayLeave(dates: Moment[]) {
    let count = 0;
    let startDate = dates[0];
    let endDate = dates[1];
    while (startDate.isSameOrBefore(endDate.clone().startOf("day"), "day")) {
      if (startDate.day() !== 0 && startDate.day() !== 6) {
        count++;
      }
      startDate = startDate.clone().add(1, "day");
    }
    return count;
  },

  countDayLeaveIncludesWeekend(dates: Moment[]) {
    var startDate = dates[0];
    var endDate = dates[1];

    return moment(endDate).diff(startDate, "day");
  },

  countWeekdays(rangeDate: any) {
    let count = 0;
    if (!rangeDate) return 0;
    if (Array.isArray(rangeDate) && rangeDate.length >= 2) {
      let start = rangeDate[0] as Moment;
      const end = rangeDate[1] as Moment;
      while (start.isSameOrBefore(end.clone().startOf("day"), "day")) {
        if (start.day() !== 0 && start.day() !== 6) {
          count++;
        }
        start = start.clone().add(1, "day");
      }
      return count;
    }
    const [start, end] = StringHelper.verticalBarSplit<string[]>(rangeDate);
    let currentDate = moment(start);
    const finalDate = moment(end);
    while (currentDate.isSameOrBefore(finalDate, "day")) {
      if (currentDate.day() !== 0 && currentDate.day() !== 6) {
        count++;
      }
      currentDate = currentDate.clone().add(1, "day");
    }
    return count;
  },

  isDateInRange(date: Moment, start: Moment, end: Moment): boolean {
    return date.isBetween(start, end, null, "[]");
  },
  getRelativeTime(dates: [Moment, Moment]): string {
    const startDate = dates[0];
    const endDate = dates[1];

    const duration = moment.duration(endDate.diff(startDate));
    moment.locale("vi");
    return duration.humanize();
  },

  duration(eVacationPeriods: Moment[][]) {
    const totalDuration = eVacationPeriods.reduce((total, current) => {
      if (DateHelper.isSameDate(current)) {
        const hours = moment.duration(current[1].diff(current[0])).hours();
        if (hours >= 8) {
          return total + moment.duration(1, "days").asMilliseconds();
        }
        return total + current[1].diff(current[0]);
      }
      const dateNum = DateHelper.countDayLeave(current);
      return total + moment.duration(dateNum, "days").asMilliseconds();
    }, 0);

    function days() {
      return moment.duration(totalDuration).asDays();
    }
    function hours() {
      return moment.duration(totalDuration).hours();
    }
    function seconds() {
      return moment.duration(totalDuration).seconds();
    }
    function milliseconds() {
      return moment.duration(totalDuration).milliseconds();
    }

    function humanize() {
      let result = "";
      if (days() > 0) result += `${days()} ngày`;
      if (hours() > 0) result += ` ${hours()} giờ`;
      return result;
    }

    return { humanize, days, hours, seconds, milliseconds };
  },

  durationIncludesWeekend(eVacationPeriods: Moment[][]) {
    const totalDuration = eVacationPeriods.reduce((total, current) => {
      if (DateHelper.isSameDate(current)) {
        const hours = moment.duration(current[1].diff(current[0])).hours();
        if (hours >= 8) {
          return total + moment.duration(1, "days").asMilliseconds();
        }
        return total + current[1].diff(current[0]);
      }
      const dateNum = DateHelper.countDayLeaveIncludesWeekend(current);
      return total + moment.duration(dateNum, "days").asMilliseconds();
    }, 0);

    function months() {
      return moment.duration(totalDuration).asMonths();
    }
    function days() {
      return moment.duration(totalDuration).asDays();
    }
    function hours() {
      return moment.duration(totalDuration).hours();
    }
    function seconds() {
      return moment.duration(totalDuration).seconds();
    }
    function milliseconds() {
      return moment.duration(totalDuration).milliseconds();
    }

    function humanize() {
      let result = "";
      if (days() > 0) result += `${days()} ngày`;
      if (hours() > 0) result += ` ${hours()} giờ`;
      return result;
    }

    return { humanize, months, days, hours, seconds, milliseconds };
  },

  multipleRangeDateToDays(data: Moment[][]) {
    const hasInValidDate = data.some((item) => !item);
    if (hasInValidDate) return 0;

    const days = DateHelper.duration(data).days();
    const hoursToDays = DateHelper.duration(data).hours() / 8;
    return days + hoursToDays;
  },

  isSameDate(dates: Moment[]) {
    return dates[0].isSame(dates[1], "day");
  },
};
